import { AssetReference } from '@domain/creativeset/element-asset';
import { WIDGET_PROPERTY_PREFIX } from '@domain/widget';
import { createElementProperty, getWidgetContentUrlOfElement } from '@studio/utils/element.utils';
import { b64DecodeUnicode, b64EncodeUnicode } from '@studio/utils/encoding';
import { uuidv4 } from '@studio/utils/id';
import { PromiseResolver } from '@studio/utils/promises';
import { resolveNamespacedCharacters } from '@studio/utils/utils';
import { deserializeWidgetPropertyValue } from '../../serialization/widget-serializer';
export const codePropertyRegex = /^(html|css|ts|js)$/;
export const designWidgetElementCodePropertyRegex = /^(html|css|js)$/;
export function stripCustomPropertyPrefix(propertyName) {
    return propertyName.replace(WIDGET_PROPERTY_PREFIX, '');
}
export function applyCustomPropertyPrefix(propertyName) {
    if (propertyName.indexOf(WIDGET_PROPERTY_PREFIX) > -1) {
        return propertyName;
    }
    return `${WIDGET_PROPERTY_PREFIX}${propertyName}`;
}
export function isWidgetReference(property) {
    return property.name === AssetReference.Widget;
}
export function isWidgetContentBlobReference(property) {
    return property.name === AssetReference.WidgetContentUrl;
}
export function isBFLibraryWidgetReference(property) {
    return property.name === AssetReference.BannerflowLibraryWidget;
}
export function isAnyWidgetReference(property) {
    return (isWidgetReference(property) ||
        isWidgetContentBlobReference(property) ||
        isBFLibraryWidgetReference(property));
}
export function isCustomProperty(property) {
    return property.name.indexOf(WIDGET_PROPERTY_PREFIX) > -1;
}
export function isWidgetCodeProperty(property) {
    return property.name !== undefined && property.name.match(codePropertyRegex) !== null;
}
export function getIframeIdFromEvent(mouseEvent) {
    var _a, _b, _c, _d, _e;
    const currentTarget = mouseEvent.currentTarget;
    if (!currentTarget) {
        return;
    }
    if (currentTarget.defaultView) {
        return (_b = (_a = currentTarget.defaultView) === null || _a === void 0 ? void 0 : _a.frameElement) === null || _b === void 0 ? void 0 : _b.id;
    }
    return (_e = (_d = (_c = currentTarget === null || currentTarget === void 0 ? void 0 : currentTarget.ownerDocument) === null || _c === void 0 ? void 0 : _c.defaultView) === null || _d === void 0 ? void 0 : _d.frameElement) === null || _e === void 0 ? void 0 : _e.id;
}
export function translateOpenEventPosition(mouseEvent, widgetInstance) {
    if (!(widgetInstance === null || widgetInstance === void 0 ? void 0 : widgetInstance.instance)) {
        return {
            x: mouseEvent.clientX,
            y: mouseEvent.clientY
        };
    }
    const { x: widgetX, y: widgetY } = widgetInstance.instance;
    // mouseEvent.clientX and clientY values are relative to the iframe
    return {
        x: mouseEvent.clientX + widgetX,
        y: mouseEvent.clientY + widgetY
    };
}
export function isValidWidgetResourceData(data) {
    return !!data && typeof data === 'object' && 'html' in data && 'css' in data && 'js' in data;
}
const widgetResourceCache = new Map();
export async function fetchWidgetFromResource(resourceUrl) {
    resourceUrl = resourceUrl.replace('http://storage-emulator:10000', 'https://blob-storage.bannerflow.local');
    const existingResource = widgetResourceCache.get(resourceUrl);
    if (existingResource) {
        await existingResource.promise;
        if (existingResource.code) {
            return existingResource.code;
        }
    }
    const loadWidgetResource = new PromiseResolver();
    const promiseObj = { promise: loadWidgetResource.promise };
    let response;
    widgetResourceCache.set(resourceUrl, promiseObj);
    try {
        response = await (await fetch(resourceUrl)).json();
    }
    catch (_a) {
        loadWidgetResource.reject();
        throw new Error('Could not fetch widget resource.');
    }
    if (!isValidWidgetResourceData(response)) {
        throw new Error('Invalid widget resource data.');
    }
    widgetResourceCache.set(resourceUrl, Object.assign(Object.assign({}, promiseObj), { code: response }));
    loadWidgetResource.resolve();
    return response;
}
export function encodeWidgetCode(widgetCode) {
    return {
        html: b64EncodeUnicode(widgetCode.html),
        css: b64EncodeUnicode(widgetCode.css),
        js: b64EncodeUnicode(widgetCode.js),
        ts: b64EncodeUnicode(widgetCode.ts)
    };
}
export const DEFAULT_WIDGET_JS = `Widget.on(WidgetEvent.PropertyChanged, () => {
    console.log(Widget.properties);
})`;
export const DEFAULT_WIDGET_HTML = `<div>Hello!</div>\n`;
export const DEFAULT_WIDGET_CSS = `html, body {
    padding: 0;
    margin: 0;
}`;
export async function getWidgetCodeOfElement(element, decoded = false) {
    var _a, _b, _c, _d;
    const resourceUrl = getWidgetContentUrlOfElement(element);
    if (resourceUrl) {
        const widgetCode = await fetchWidgetFromResource(resourceUrl);
        return decoded ? widgetCode : encodeWidgetCode(widgetCode);
    }
    const isElementCreated = Boolean(element.id);
    // Code properties should always be encoded
    const html = isElementCreated
        ? (_a = element.properties.find(({ name }) => name === 'html')) === null || _a === void 0 ? void 0 : _a.value
        : b64EncodeUnicode(DEFAULT_WIDGET_HTML);
    const css = isElementCreated
        ? (_b = element.properties.find(({ name }) => name === 'css')) === null || _b === void 0 ? void 0 : _b.value
        : b64EncodeUnicode(DEFAULT_WIDGET_CSS);
    const js = isElementCreated
        ? (_c = element.properties.find(({ name }) => name === 'js')) === null || _c === void 0 ? void 0 : _c.value
        : b64EncodeUnicode(DEFAULT_WIDGET_JS);
    const ts = isElementCreated
        ? (_d = element.properties.find(({ name }) => name === 'ts')) === null || _d === void 0 ? void 0 : _d.value
        : b64EncodeUnicode(DEFAULT_WIDGET_JS);
    return {
        html: decoded ? b64DecodeUnicode(html) : html,
        css: decoded ? b64DecodeUnicode(css) : css,
        js: decoded ? b64DecodeUnicode(js) : js,
        ts: decoded ? b64DecodeUnicode(ts) : ts
    };
}
export async function updateOrCreateWidgetElementCodeProperties(element) {
    const resourceUrl = getWidgetContentUrlOfElement(element);
    if (!resourceUrl) {
        // No widget resource URL, code properties are already present
        return;
    }
    const { html, js, css } = await getWidgetCodeOfElement(element);
    const htmlProperty = element.properties.find(({ name }) => name === 'html');
    const cssProperty = element.properties.find(({ name }) => name === 'css');
    const jsProperty = element.properties.find(({ name }) => name === 'js');
    if (htmlProperty) {
        htmlProperty.value = html;
    }
    else {
        element.properties.push(createElementProperty({
            id: uuidv4(),
            name: 'html',
            value: html,
            unit: 'string'
        }));
    }
    if (cssProperty) {
        cssProperty.value = css;
    }
    else {
        element.properties.push(createElementProperty({
            id: uuidv4(),
            name: 'css',
            value: css,
            unit: 'string'
        }));
    }
    if (jsProperty) {
        jsProperty.value = js;
    }
    else {
        element.properties.push(createElementProperty({
            id: uuidv4(),
            name: 'js',
            value: js,
            unit: 'string'
        }));
    }
}
export function mapElementToCustomWidgetProperties(element) {
    return element.properties
        .filter(isCustomProperty)
        .map((property) => {
        const value = deserializeWidgetPropertyValue(property);
        return {
            name: stripCustomPropertyPrefix(property.name),
            label: resolveNamespacedCharacters(property.label),
            unit: property.unit,
            value: value,
            versionPropertyId: property.versionPropertyId
        };
    });
}
export function isWidgetImage(value) {
    return (typeof value === 'object' &&
        'id' in value &&
        'src' in value &&
        typeof value.id === 'string' &&
        typeof value.src === 'string');
}
export function isWidgetFont(value) {
    return (typeof value === 'object' &&
        'id' in value &&
        'src' in value &&
        'style' in value &&
        typeof value.id === 'string' &&
        typeof value.src === 'string');
}
